import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  OPEN_CONSENT_EVENT,
  getConsent,
  gtmEnabled,
  setConsent,
  type ConsentChoice,
} from "@/lib/consent";

/**
 * Cookie consent banner for the Google tags (Consent Mode v2 — see
 * lib/consent.ts). Shown until the visitor chooses; re-opened from the footer's
 * "Cookie settings" button. Renders nothing when GTM isn't configured, since
 * then the site sets no tracking cookies.
 *
 * Accessibility: a labelled `region` (not a modal — the page stays usable), two
 * equally prominent buttons so rejecting is as easy as accepting, and while it
 * is open the page gets bottom scroll-padding equal to its height so a focused
 * element is never scrolled under it (WCAG 2.4.11 Focus Not Obscured). Mounted
 * client-side only (it depends on localStorage), so the pre-rendered HTML and
 * hydration match.
 */
export function ConsentBanner() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!gtmEnabled()) return;
    if (getConsent() === null) setOpen(true);
    const reopen = () => {
      returnFocusTo.current = document.activeElement as HTMLElement | null;
      setOpen(true);
      // Take the keyboard user to the choice they asked to change.
      requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>("h2")?.focus());
    };
    window.addEventListener(OPEN_CONSENT_EVENT, reopen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, reopen);
  }, []);

  useEffect(() => {
    if (!open || !ref.current) return;
    const root = document.documentElement;
    const apply = () => {
      root.style.scrollPaddingBottom = `${ref.current?.offsetHeight ?? 0}px`;
    };
    apply();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : null;
    ro?.observe(ref.current);
    return () => {
      ro?.disconnect();
      root.style.scrollPaddingBottom = "";
    };
  }, [open]);

  if (!open) return null;

  const choose = (choice: ConsentChoice) => {
    setConsent(choice);
    setOpen(false);
    returnFocusTo.current?.focus?.();
    returnFocusTo.current = null;
  };

  return (
    <div
      ref={ref}
      role="region"
      aria-labelledby="consent-heading"
      className="fixed inset-x-0 bottom-0 z-[60] border-t bg-background shadow-[0_-4px_16px_rgba(0,0,0,0.08)]"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 md:flex-row md:items-center md:justify-between md:px-8 lg:px-16">
        <div className="max-w-3xl">
          <h2
            id="consent-heading"
            tabIndex={-1}
            className="font-display text-base font-bold text-foreground focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring rounded-sm"
          >
            Cookies for measuring our ads
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            With your permission, Google Analytics and Google Ads set cookies so we can see which
            ads bring enquiries. The site works the same either way.{" "}
            <Link
              to="/privacy"
              className="font-medium text-primary underline underline-offset-4 focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring rounded-sm"
            >
              Privacy policy
            </Link>
          </p>
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          <Button variant="hero-outline" className="min-h-[44px]" onClick={() => choose("denied")}>
            Reject
          </Button>
          <Button variant="hero" className="min-h-[44px]" onClick={() => choose("granted")}>
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ConsentBanner;
